/** 선택된 목차(노트)의 표 비교 상세.

 * 매칭된 표는 Workbook/IXD 두 개의 트리를 나란히 정렬해 보여준다 — 한쪽에만 있는
 * 노드는 반대편에 ghost 행을 넣어 행 정렬을 유지한다. 추가/삭제된 표도 자기 쪽
 * 트리를 그대로 보여준다.
 */

import type {
  IxdWorkbookCompareResponse,
  MatchedTableDiff,
  MergedTreeNode,
  TableRef,
} from "@/apis/ixd-workbook-compare/route";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/styles/ui/popover";
import { CheckCircle2, ChevronRight, ExternalLink } from "lucide-react";
import { useState } from "react";
import { isPlKey, type NoteEntry } from "./compare-sidebar";

/** 해당 workbook 표 태깅화면(mapping)을 새 탭으로 여는 버튼 */
const WorkbookTableLink = ({
  workbookId,
  sheetId,
  tableId,
}: {
  workbookId: string | null;
  sheetId: string | null;
  tableId: string | null;
}) => {
  if (!workbookId || !sheetId || !tableId) return null;
  return (
    <a
      href={`/mapping/${workbookId}/${sheetId}?table=${tableId}`}
      target="_blank"
      rel="noreferrer"
      className="ml-auto flex shrink-0 items-center gap-1 rounded-[6px] border border-line-normal px-2 py-1 text-xs text-label-normal transition-colors hover:bg-background-neutral-neutral"
    >
      <ExternalLink className="size-3" />표 태깅화면으로 가기
    </a>
  );
};

/** 클릭하면 tag key를 보여주는 팝오버 (다시 클릭하거나 바깥 클릭 시 닫힘) */
const KeyPopover = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement;
}) => (
  <Popover>
    <PopoverTrigger asChild>{children}</PopoverTrigger>
    <PopoverContent
      side="top"
      align="start"
      className="w-auto max-w-none border-none bg-neutral-900 px-2 py-1.5 font-mono text-[11px] text-white"
    >
      {label}
    </PopoverContent>
  </Popover>
);

const nodeSumChanged = (node: MergedTreeNode) =>
  node.workbookIsSum !== null &&
  node.ixdIsSum !== null &&
  node.workbookIsSum !== node.ixdIsSum;

const nodeDataTypeChanged = (node: MergedTreeNode) =>
  node.workbookDataType !== null &&
  node.ixdDataType !== null &&
  node.workbookDataType !== node.ixdDataType;

const Badge = ({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) => (
  <span
    className={cn(
      "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
      className,
    )}
  >
    {children}
  </span>
);

type TreeSide = "workbook" | "ixd";

/** 표 전체 금액 단위 배지 — 항상 표시, 변경 시에만 강조 */
const UnitBadge = ({
  workbookUnitLabel,
  ixdUnitLabel,
  changed,
}: {
  workbookUnitLabel: string | null;
  ixdUnitLabel: string | null;
  changed: boolean;
}) => {
  if (!workbookUnitLabel && !ixdUnitLabel) return null;
  if (changed) {
    return (
      <Badge className="bg-amber-100 text-amber-700">
        단위: WB {workbookUnitLabel ?? "없음"} → IXD {ixdUnitLabel ?? "없음"}
      </Badge>
    );
  }
  const label =
    workbookUnitLabel && ixdUnitLabel
      ? workbookUnitLabel
      : workbookUnitLabel
        ? `WB ${workbookUnitLabel}`
        : `IXD ${ixdUnitLabel}`;
  return (
    <Badge className="bg-neutral-100 text-neutral-500">단위: {label}</Badge>
  );
};

const GhostCell = ({ depth }: { depth: number }) => (
  <div
    className="my-0.5 rounded border border-dashed border-neutral-200 bg-neutral-50/60"
    style={{ marginLeft: `${8 + depth * 20}px` }}
  >
    <span className="px-2 text-xs text-neutral-300 select-none">—</span>
  </div>
);

const TreeCell = ({ node, side }: { node: MergedTreeNode; side: TreeSide }) => {
  const presentOnSide =
    node.status === "same" ||
    (side === "workbook"
      ? node.status === "workbook_only"
      : node.status === "ixd_only");

  if (!presentOnSide) {
    return <GhostCell depth={node.depth} />;
  }

  const label = side === "workbook" ? node.workbookLabel : node.ixdLabel;
  const isSum = side === "workbook" ? node.workbookIsSum : node.ixdIsSum;
  const isExtended =
    side === "workbook" ? node.workbookIsExtended : node.ixdIsExtended;
  const dataType =
    side === "workbook" ? node.workbookDataType : node.ixdDataType;
  const removed = node.status === "workbook_only";
  const added = node.status === "ixd_only";
  const dataTypeChanged = nodeDataTypeChanged(node);
  // 별칭(label)만 다른 것은 변경으로 강조하지 않는다 — 양쪽 열에 라벨이 그대로 보인다.
  const changed = nodeSumChanged(node) || dataTypeChanged;
  const inferredComparable =
    node.workbookIsExtended &&
    node.workbookDataType !== null &&
    node.ixdDataType !== null;

  const sourceTagId =
    (side === "workbook" ? node.workbookSourceTagId : node.ixdSourceTagId) ??
    node.tagKey;

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded px-2 py-1 text-sm",
        removed && "bg-rose-50",
        added && "bg-emerald-50",
        changed && !removed && !added && "bg-amber-50",
      )}
      style={{ paddingLeft: `${8 + node.depth * 20}px` }}
    >
      <KeyPopover label={sourceTagId}>
        <span
          className={cn(
            "min-w-0 cursor-pointer truncate",
            removed
              ? "text-rose-600 line-through"
              : added
                ? "text-emerald-700"
                : "text-label-normal",
          )}
        >
          {label ?? node.tagKey}
        </span>
      </KeyPopover>

      {isExtended && (
        <Badge className="bg-indigo-100 text-indigo-700">확장</Badge>
      )}
      {nodeSumChanged(node) && (
        <Badge className="bg-amber-100 text-amber-700">
          합계열 {isSum ? "사용" : "미사용"}
        </Badge>
      )}
      {dataType && (dataType !== "DOMAIN_ITEM" || dataTypeChanged) && (
        <Badge
          className={cn(
            dataTypeChanged
              ? "bg-amber-100 text-amber-700"
              : "bg-neutral-100 text-neutral-500",
          )}
        >
          {node.workbookIsExtended && side === "workbook"
            ? `추론 ${dataType}`
            : dataType}
        </Badge>
      )}
      {side === "ixd" && node.ixdForeignCurrencyUnit && (
        <Badge className="bg-sky-100 text-sky-700">
          외화 {node.ixdForeignCurrencyUnit}
        </Badge>
      )}
      {inferredComparable && side === "workbook" && (
        <Badge
          className={
            dataTypeChanged
              ? "bg-rose-100 text-rose-700"
              : "bg-emerald-100 text-emerald-700"
          }
        >
          {dataTypeChanged ? "추론 불일치" : "추론 일치"}
        </Badge>
      )}
    </div>
  );
};

const SideBySideTree = ({ nodes }: { nodes: MergedTreeNode[] }) => (
  <div className="rounded-[8px] border border-neutral-100">
    <div className="grid grid-cols-2 gap-2 border-b border-neutral-100 px-2 py-1.5">
      <span className="caption-2-regular font-medium text-label-alternative">
        Workbook
      </span>
      <span className="caption-2-regular font-medium text-label-alternative">
        IXD
      </span>
    </div>
    <div className="py-1">
      {nodes.map((node, index) => (
        <div
          key={`${node.tagKey}:${node.depth}:${index}`}
          className="grid grid-cols-2 items-stretch gap-2"
        >
          <TreeCell node={node} side="workbook" />
          <TreeCell node={node} side="ixd" />
        </div>
      ))}
    </div>
  </div>
);

const OnlyTableCard = ({
  table,
  side,
  workbookId,
}: {
  table: TableRef;
  side: TreeSide;
  workbookId: string | null;
}) => (
  <section className="rounded-[10px] border border-neutral-200 bg-white p-4">
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <KeyPopover
        label={`${table.sourceTableTagKey ?? table.tableTagKey} · ${table.roleKey}`}
      >
        <h3 className="label-1-normal-medium cursor-pointer text-label-normal">
          {table.labelKo}
        </h3>
      </KeyPopover>
      {side === "workbook" ? (
        <Badge className="bg-rose-100 text-rose-700">WB에만 (표 삭제)</Badge>
      ) : (
        <Badge className="bg-emerald-100 text-emerald-700">
          IXD에만 (표 추가)
        </Badge>
      )}
      <UnitBadge
        workbookUnitLabel={table.unitLabel}
        ixdUnitLabel={table.unitLabel}
        changed={false}
      />
      <WorkbookTableLink
        workbookId={workbookId}
        sheetId={table.workbookSheetId}
        tableId={table.workbookTableId}
      />
    </div>
    <SideBySideTree nodes={table.tree} />
  </section>
);

/** 변경 없는 매칭 표 — 클릭하면 트리를 펼치고 접는다. */
const UnchangedTableCard = ({
  diff,
  workbookId,
}: {
  diff: MatchedTableDiff;
  workbookId: string | null;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="rounded-[10px] border border-neutral-200 bg-white">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded ? "true" : "false"}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-neutral-400 transition-transform",
              expanded && "rotate-90",
            )}
          />
          <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
          <span className="label-1-normal-medium truncate text-label-normal">
            {diff.labelKo}
          </span>
          <Badge className="bg-neutral-100 text-neutral-500">동일</Badge>
          {diff.isMigrationMatch && (
            <Badge className="bg-violet-100 text-violet-700">
              마이그레이션 필요
            </Badge>
          )}
          <UnitBadge
            workbookUnitLabel={diff.workbookUnitLabel}
            ixdUnitLabel={diff.ixdUnitLabel}
            changed={false}
          />
        </button>
        <WorkbookTableLink
          workbookId={workbookId}
          sheetId={diff.workbookSheetId}
          tableId={diff.workbookTableId}
        />
      </div>
      {expanded && (
        <div className="px-4 pb-4">
          <SideBySideTree nodes={diff.mergedTree} />
        </div>
      )}
    </section>
  );
};

const MatchedTableCard = ({
  diff,
  workbookId,
}: {
  diff: MatchedTableDiff;
  workbookId: string | null;
}) => {
  if (!diff.hasChanges) {
    return (
      <UnchangedTableCard diff={diff} workbookId={workbookId} />
    );
  }

  return (
    <section className="rounded-[10px] border border-neutral-200 bg-white p-4">
      <div className="mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <KeyPopover
            label={`WB ${diff.workbookSourceTableTagKey ?? diff.tableTagKey} (${diff.workbookRoleKey}) · IXD ${diff.ixdSourceTableTagKey ?? diff.tableTagKey} (${diff.ixdRoleKey})`}
          >
            <h3 className="label-1-normal-medium cursor-pointer text-label-normal">
              {diff.labelKo}
            </h3>
          </KeyPopover>
          {diff.isMigrationMatch && (
            <Badge className="bg-violet-100 text-violet-700">
              마이그레이션 필요
            </Badge>
          )}
          {diff.transposeChanged && (
            <Badge className="bg-amber-100 text-amber-700">
              행/열 전환: WB {diff.workbookIsTransposed ? "전환" : "기본"} → IXD{" "}
              {diff.ixdIsTransposed ? "전환" : "기본"}
            </Badge>
          )}
          {diff.axisOrderChanged && (
            <Badge className="bg-amber-100 text-amber-700">축 순서 변경</Badge>
          )}
          <UnitBadge
            workbookUnitLabel={diff.workbookUnitLabel}
            ixdUnitLabel={diff.ixdUnitLabel}
            changed={diff.unitChanged}
          />
          <WorkbookTableLink
            workbookId={workbookId}
            sheetId={diff.workbookSheetId}
            tableId={diff.workbookTableId}
          />
        </div>
        {diff.axisOrderChanged && (
          <div className="mt-1.5 flex flex-col gap-0.5 text-xs text-label-alternative">
            <span>WB: {diff.workbookAxisOrder.join(" → ") || "(없음)"}</span>
            <span>IXD: {diff.ixdAxisOrder.join(" → ") || "(없음)"}</span>
          </div>
        )}
      </div>
      <SideBySideTree nodes={diff.mergedTree} />
    </section>
  );
};

export const NoteDiffDetail = ({
  note,
  result,
  workbookId,
}: {
  note: NoteEntry;
  result: IxdWorkbookCompareResponse;
  workbookId: string | null;
}) => {
  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div>
        <p className="caption-2-regular text-label-alternative">
          {note.groupLabel}
        </p>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
          {note.noteLabel}
          {isPlKey(note.id) && (
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-normal text-neutral-500">
              {note.id}
            </code>
          )}
        </h2>
      </div>

      {note.tables.map((ref) => {
        if (ref.kind === "matched") {
          const diff = result.matchedTables[ref.index];
          return diff ? (
            <MatchedTableCard
              key={`matched:${ref.index}`}
              diff={diff}
              workbookId={workbookId}
            />
          ) : null;
        }
        if (ref.kind === "workbook") {
          const table = result.tablesOnlyInWorkbook[ref.index];
          return table ? (
            <OnlyTableCard
              key={`workbook:${ref.index}`}
              table={table}
              side="workbook"
              workbookId={workbookId}
            />
          ) : null;
        }
        const table = result.tablesOnlyInIxd[ref.index];
        return table ? (
          <OnlyTableCard
            key={`ixd:${ref.index}`}
            table={table}
            side="ixd"
            workbookId={workbookId}
          />
        ) : null;
      })}
    </div>
  );
};
